import React, { useState, useEffect, useRef } from 'react';
import './ChatWidget.css';

const WS_PORT = process.env.REACT_APP_WS_PORT || '4003';
const ADMIN_WS_URL = process.env.REACT_APP_ADMIN_WS_URL || `ws://${window.location.hostname || 'localhost'}/ws`;
const SESSIONS_STORAGE_KEY = 'federal_ai_chat_sessions';
const USER_INFO_KEY = 'federal_ai_user_info';
const CHAT_DEVICE_ID_KEY = 'kamelot_chat_device_id';
const CHAT_IDENTITY_KEY = 'kamelot_chat_identity';

const getDeviceId = () => {
  let deviceId = localStorage.getItem(CHAT_DEVICE_ID_KEY);
  if (!deviceId) {
    deviceId = (window.crypto?.randomUUID?.() || `device_${Date.now()}_${Math.random().toString(36).substr(2, 10)}`);
    localStorage.setItem(CHAT_DEVICE_ID_KEY, deviceId);
  }
  return deviceId;
};

const getBrowserInfo = () => {
  const userAgent = navigator.userAgent;
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
  const deviceInfo = {
    userAgent,
    deviceType: isMobile ? 'mobile' : 'desktop',
    language: navigator.language,
    platform: navigator.platform,
    screen: `${window.screen?.width}x${window.screen?.height}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
  };
  return deviceInfo;
};

const getChatIdentity = () => {
  const deviceInfo = getBrowserInfo();
  const saved = (() => {
    try {
      return JSON.parse(localStorage.getItem(CHAT_IDENTITY_KEY) || '{}');
    } catch (e) {
      return {};
    }
  })();

  return {
    deviceId: getDeviceId(),
    identityId: saved.identityId || '',
    sessionId: saved.sessionId || '',
    ipHash: saved.ipHash || '',
    deviceInfo
  };
};

const saveChatIdentity = (identity) => {
  localStorage.setItem(CHAT_IDENTITY_KEY, JSON.stringify({
    ...getChatIdentity(),
    ...identity,
    deviceInfo: identity.deviceInfo || getBrowserInfo()
  }));
};

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'assistant', content: '🛡️ Federal AI Online\n\nI provide info on Security, Government & Law Enforcement.\n\nClick "Human Agent" to start a live chat session.' }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isHumanMode, setIsHumanMode] = useState(false);
  const [currentNav, setCurrentNav] = useState('security');
  const [queuePosition, setQueuePosition] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [showUserForm, setShowUserForm] = useState(false);
  const [userName, setUserName] = useState('');
  const [userPhone, setUserPhone] = useState('+233');
  const [chatHistory, setChatHistory] = useState({});
  const [showHistory, setShowHistory] = useState(false);
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);
  const wsConnectPromiseRef = useRef(null);
  const isMountedRef = useRef(true);

  const navContent = {
    security: "🛡️ SECURITY PROTOCOLS\n\n• Threat assessment frameworks\n• Critical infrastructure protection\n• Access control best practices\n• Risk management strategies",
    government: "🏛️ GOVERNMENT SERVICES\n\n• Emergency management\n• Administrative procedures\n• Interagency coordination\n• Regulatory compliance",
    law: "⚖️ LAW ENFORCEMENT\n\n• Standard operating procedures\n• Incident response protocols\n• Evidence handling\n• Legal frameworks",
    resources: "📁 OFFICIAL RESOURCES\n\n• Security templates\n• Service directories\n• Procedural guides\n• Response frameworks"
  };

  // Load user info and chat history on mount
  useEffect(() => {
    // Load user info
    const savedUser = localStorage.getItem(USER_INFO_KEY);
    if (savedUser) {
      const userData = JSON.parse(savedUser);
      setUserName(userData.name || '');
      setUserPhone(userData.phone || '+233');
    }
    
    const savedIdentity = (() => {
      try {
        return JSON.parse(localStorage.getItem(CHAT_IDENTITY_KEY) || '{}');
      } catch (e) {
        return {};
      }
    })();
    if (savedIdentity.sessionId) {
      setSessionId(savedIdentity.sessionId);
    }
    
    // Load all chat sessions
    const savedSessions = localStorage.getItem(SESSIONS_STORAGE_KEY);
    if (savedSessions) {
      try {
        const sessions = JSON.parse(savedSessions);
        setChatHistory(sessions);
        console.log('[Federal AI] Loaded chat history:', Object.keys(sessions).length, 'sessions');
      } catch (e) {
        console.log('[Federal AI] Failed to load chat history:', e);
      }
    }
  }, []);

  // Save user info
  const saveUserInfo = (name, phone) => {
    localStorage.setItem(USER_INFO_KEY, JSON.stringify({ name, phone }));
  };

  // Save chat history
  const saveChatHistory = (phone, sessionData) => {
    const history = { ...chatHistory };
    history[phone] = sessionData;
    setChatHistory(history);
    localStorage.setItem(SESSIONS_STORAGE_KEY, JSON.stringify(history));
  };

  // Load session for specific phone
  const loadSessionForPhone = (phone) => {
    const history = { ...chatHistory };
    if (history[phone] && history[phone].messages && history[phone].messages.length > 0) {
      return history[phone];
    }
    return null;
  };

  // Save to localStorage whenever messages or session change
  useEffect(() => {
    if (sessionId && userPhone && userPhone !== '+233') {
      const data = {
        sessionId,
        messages,
        isHumanMode,
        lastActive: Date.now()
      };
      saveChatHistory(userPhone, data);
    }
  }, [sessionId, messages, isHumanMode, userPhone]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, []);

  const sendWebSocketMessage = (payload) => {
    const socket = wsRef.current;
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      console.log('[Federal AI] WebSocket not open yet');
      return false;
    }

    try {
      socket.send(JSON.stringify(payload));
      return true;
    } catch (err) {
      console.error('[Federal AI] WebSocket send failed:', err);
      return false;
    }
  };

  const buildLiveChatPayload = (type, message = '') => {
    const identity = getChatIdentity();
    return {
      type,
      data: {
        sessionId: sessionId || identity.sessionId,
        identityId: identity.identityId,
        deviceId: identity.deviceId,
        message,
        timestamp: Date.now(),
        phone: userPhone,
        name: userName,
        userAgent: identity.deviceInfo.userAgent,
        ipHash: identity.ipHash,
        deviceInfo: identity.deviceInfo
      }
    };
  };

  const sendLiveChatMessage = (message) => {
    const payload = buildLiveChatPayload('customer_message', message);
    if (sendWebSocketMessage(payload)) {
      return true;
    }

    return connectWebSocket().then(() => sendWebSocketMessage(payload));
  };

  const requestHumanAgent = (message = 'Customer requested human agent') => {
    const requestPayload = buildLiveChatPayload('request_agent', message);
    const messagePayload = buildLiveChatPayload('customer_message', message);

    const sendBoth = () => {
      const requestSent = sendWebSocketMessage(requestPayload);
      setTimeout(() => sendWebSocketMessage(messagePayload), 250);
      return requestSent;
    };

    if (sendWebSocketMessage(requestPayload)) {
      setTimeout(() => sendWebSocketMessage(messagePayload), 250);
      return Promise.resolve(true);
    }

    return connectWebSocket().then(sendBoth);
  };

  const connectWebSocket = () => {
    if (wsConnectPromiseRef.current) {
      return wsConnectPromiseRef.current;
    }

    const existingSocket = wsRef.current;
    if (existingSocket && existingSocket.readyState === WebSocket.OPEN) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      try {
        const socket = new WebSocket(ADMIN_WS_URL);
        const connectPromise = new Promise((innerResolve, innerReject) => {
          wsRef.current = socket;

          socket.onopen = () => {
            console.log('[Federal AI] WebSocket connected');
            const identity = getChatIdentity();
            const regData = {
              type: 'register',
              clientType: 'customer',
              deviceId: identity.deviceId,
              identityId: identity.identityId,
              sessionId: sessionId || identity.sessionId,
              phone: userPhone,
              name: userName,
              userAgent: identity.deviceInfo.userAgent,
              deviceInfo: identity.deviceInfo
            };
            if (sessionId && userPhone) {
              regData.sessionId = sessionId;
              regData.phone = userPhone;
            }
            if (socket.readyState === WebSocket.OPEN) {
              socket.send(JSON.stringify(regData));
            }
            innerResolve();
          };
          
          socket.onmessage = (event) => {
            try {
              const data = JSON.parse(event.data);
              console.log('[Federal AI] Received:', data.type, 'sessionId:', data.data?.sessionId, 'mySessionId:', sessionId);
              
              if (data.type === 'identity_registered') {
                saveChatIdentity(data.data || {});
                if (data.data?.sessionId) {
                  setSessionId(data.data.sessionId);
                }
                return;
              }
              
              if (data.type === 'typing_indicator' && data.data?.sessionId === sessionId) {
                return;
              }
              
              // Handle session resumed with previous messages from backend
              if (data.type === 'session_created' && data.data?.sessionId) {
                console.log('[Federal AI] Session state:', data.data);
                saveChatIdentity({
                  sessionId: data.data.sessionId,
                  identityId: data.data.identityId,
                  ipHash: data.data.customerInfo?.ipHash
                });
                setSessionId(data.data.sessionId);
                if (data.data.previousMessages && data.data.previousMessages.length > 0) {
                  setMessages(data.data.previousMessages);
                  if (data.data.isResumed) {
                    addMessage(`🔄 Previous conversation restored (${data.data.previousMessages.length} messages). An agent will respond shortly.`, 'assistant');
                  }
                }
                setQueuePosition(data.data.position || 0);
                return;
              }
              
              // Handle session_resumed notification
              if (data.type === 'session_resumed') {
                console.log('[Federal AI] Session resumed notification:', data.data);
                addMessage(`🔄 Chat history restored (${data.data.messageCount} previous messages)`, 'assistant');
                return;
              }
              
              if (data.type === 'agent_message') {
                if (data.data?.message) {
                  addMessage(data.data.message, 'assistant');
                }
              } else if (data.type === 'new_message' && data.data?.sessionId === sessionId) {
                addMessage(data.data.message, 'assistant');
              } else if (data.type === 'new_message' && data.data?.sessionId) {
                addMessage(data.data.message, 'assistant');
              } else if (data.type === 'queue_update' && data.data?.sessionId === sessionId) {
                setQueuePosition(data.data.position);
              } else if (data.type === 'agent_joined' && data.data?.sessionId === sessionId) {
                addMessage(data.data.message, 'assistant');
                setIsHumanMode(true);
                setQueuePosition(null);
              } else if (data.type === 'agent_joined' && data.data?.message) {
                addMessage(data.data.message, 'assistant');
                setIsHumanMode(true);
                setQueuePosition(null);
              } else if (data.type === 'session_created' && data.data?.sessionId === sessionId) {
                setQueuePosition(data.data.position || 1);
              } else if (data.type === 'session_ended') {
                addMessage('📋 This conversation has ended. Thank you for contacting us.', 'assistant');
                setIsHumanMode(false);
              } else if (data.type === 'connected') {
                console.log('[Federal AI] WS ready');
              }
            } catch (err) {
              console.error('[Federal AI] Parse error:', err);
            }
          };
          
          socket.onclose = () => {
            console.log('[Federal AI] WebSocket disconnected');
            if (wsRef.current === socket) {
              wsRef.current = null;
            }
            wsConnectPromiseRef.current = null;
            setTimeout(() => {
              if (isOpen && isMountedRef.current && wsRef.current !== socket) {
                connectWebSocket();
              }
            }, 3000);
          };
          
          socket.onerror = (err) => {
            console.error('[Federal AI] WebSocket error:', err);
            if (socket.readyState === WebSocket.CONNECTING) {
              wsConnectPromiseRef.current = null;
              innerReject(err);
            }
          };
        });

        wsConnectPromiseRef.current = connectPromise;
        connectPromise.then(resolve).catch(reject);
      } catch (err) {
        console.error('[Federal AI] Failed to connect:', err);
        wsConnectPromiseRef.current = null;
        reject(err);
      }
    });
  };

  // Handle human agent button click - show form first
  const handleHumanAgent = () => {
    proceedToHumanAgent();
    if (!userPhone || userPhone === '+233' || !userName) {
      setShowUserForm(true);
    }
  };

  // Proceed after user info is provided
  const proceedToHumanAgent = () => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      connectWebSocket().then(() => {
        createSessionAndRequestAgent();
      }).catch(err => {
        createLocalSession();
      });
    } else {
      createSessionAndRequestAgent();
    }
  };

  // Submit user form
  const handleUserFormSubmit = (e) => {
    e.preventDefault();
    if (!userName.trim() || !userPhone.trim()) return;
    
    saveUserInfo(userName.trim(), userPhone.trim());
    setShowUserForm(false);
    proceedToHumanAgent();
  };

  // Save user and show form
  const handleSaveAndContinue = (e) => {
    e.preventDefault();
    const phone = userPhone.trim();
    if (!userName.trim() || !phone || phone === '+233' || phone.length <= 4) return;
    
    saveUserInfo(userName.trim(), phone);
    setShowUserForm(false);
    
    // Check if we have previous chat for this phone
    const previousChat = loadSessionForPhone(phone);
    if (previousChat) {
      setSessionId(previousChat.sessionId);
      setMessages(previousChat.messages);
      setIsHumanMode(previousChat.isHumanMode || false);
    }
    
    proceedToHumanAgent();
  };

  const createSessionAndRequestAgent = () => {
    requestHumanAgent().then((sent) => {
      if (sent) {
        setIsHumanMode(true);
        const existingChat = loadSessionForPhone(userPhone);
        if (existingChat && existingChat.messages && existingChat.messages.length > 0) {
          addMessage('🔄 Resuming your previous conversation...', 'assistant');
        } else {
          addMessage('🟢 Connecting to live agent...\n\nPlease wait while we connect you to an available agent.', 'assistant');
        }
      } else {
        createLocalSession();
      }
    }).catch(createLocalSession);
  };

  const createLocalSession = () => {
    const newSessionId = `visitor-${Date.now()}-${Math.random().toString(36).substr(2,6)}`;
    setSessionId(newSessionId);
    setIsHumanMode(true);
    addMessage('🟢 Live Human Agent Connected 🟢\n\nHello, I\'m here to assist you. How can I help you today?', 'assistant');
  };

  const addMessage = (content, role = 'assistant') => {
    setMessages(prev => [...prev, { role, content }]);
  };

  const getAIResponse = async (userMessage) => {
    try {
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          messages: [
            { role: 'system', content: 'You are Federal AI Assistant for Security, Government, and Law Enforcement. Provide clear, factual answers. Be professional and concise.' },
            { role: 'user', content: userMessage }
          ]
        })
      });
      const data = await response.json();
      if (data.choices?.[0]?.message?.content) {
        return data.choices[0].message.content;
      }
      return 'Service momentarily unavailable. Please try again.';
    } catch (err) {
      return 'Secure channel active. Please rephrase your query.';
    }
  };

  const handleSend = async () => {
    if (!input.trim()) return;
    
    const userMsg = input.trim();
    setInput('');
    addMessage(userMsg, 'user');

    if (isHumanMode && sessionId) {
      const sent = sendWebSocketMessage(buildLiveChatPayload('customer_message', userMsg));
      if (sent) {
        console.log('[Federal AI] Message sent to admin:', sessionId);
        return;
      }
    } else if (!isHumanMode) {
      sendLiveChatMessage(userMsg).then((sent) => {
        if (sent) {
          setIsHumanMode(true);
          return;
        }
        setIsLoading(true);
        return getAIResponse(userMsg).then((response) => {
          addMessage(response, 'assistant');
          setIsLoading(false);
        });
      }).catch(() => {
        setIsLoading(true);
        getAIResponse(userMsg).then((response) => {
          addMessage(response, 'assistant');
          setIsLoading(false);
        });
      });
      return;
    }

    setIsLoading(true);
    const response = await getAIResponse(userMsg);
    addMessage(response, 'assistant');
    setIsLoading(false);
  };

  const handleInputChange = (e) => {
    setInput(e.target.value);
    if (isHumanMode && sessionId) {
      sendWebSocketMessage(buildLiveChatPayload('typing_indicator'));
    }
  };

  const handleNewChat = () => {
    const previousSessionId = sessionId;
    setMessages([
      { role: 'assistant', content: '🛡️ New Conversation Started\n\nI\'m ready to assist with Security, Government & Law Enforcement topics.\n\nAn agent request has been sent. Please wait for an agent to accept the chat.' }
    ]);
    setIsHumanMode(true);
    setSessionId(null);
    setQueuePosition(null);

    if (previousSessionId) {
      sendWebSocketMessage({ type: 'session_closed', data: { sessionId: previousSessionId } });
    }

    requestHumanAgent('New chat requested').then((sent) => {
      if (!sent) {
        createLocalSession();
      }
    }).catch(createLocalSession);
  };

  const handleNav = (navId) => {
    setCurrentNav(navId);
    const content = navContent[navId];
    addMessage(content, 'assistant');
  };

  const toggleChat = () => {
    const newIsOpen = !isOpen;
    setIsOpen(newIsOpen);
    if (newIsOpen && !wsRef.current) {
      connectWebSocket().catch(err => console.log('[Federal AI] WS connection failed'));
    }
  };

  // Format phone number
  const formatPhone = (phone) => {
    if (!phone) return 'Unknown';
    return phone.length > 10 ? phone.slice(-10) : phone;
  };

  // Get session preview
  const getSessionPreview = (session) => {
    if (!session.messages || session.messages.length === 0) return 'No messages';
    const lastMsg = session.messages[session.messages.length - 1];
    const preview = lastMsg.content.substring(0, 30);
    return lastMsg.role === 'user' ? `You: ${preview}...` : `Agent: ${preview}...`;
  };

  return (
    <>
      {/* User Info Popup Form */}
      {showUserForm && (
        <div className="chat-popup-overlay">
          <div className="chat-popup-form">
            <div className="popup-header">
              <div className="popup-icon">
                <i className="fas fa-user-circle"></i>
              </div>
              <h3>Welcome to Federal AI</h3>
              <p>Please provide your details to continue</p>
            </div>
            <form onSubmit={handleSaveAndContinue}>
              <div className="form-group">
                <label>
                  <i className="fas fa-user"></i> Your Name
                </label>
                <input
                  type="text"
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                  placeholder="Enter your name"
                  required
                />
              </div>
              <div className="form-group">
                <label>
                  <i className="fas fa-phone"></i> Phone Number
                </label>
                <div className="phone-input">
                  <span className="country-code">+233</span>
                  <input
                    type="tel"
                    value={userPhone === '+233' ? '' : userPhone.replace('+233', '')}
                    onChange={(e) => setUserPhone('+233' + e.target.value.replace(/\D/g, '').slice(0, 9))}
                    placeholder="50 123 4567"
                    required
                  />
                </div>
              </div>
              <button type="submit" className="popup-submit-btn">
                <i className="fas fa-paper-plane"></i> Continue to Chat
              </button>
            </form>
            <div className="popup-footer">
              <p>Your info is saved locally for chat history</p>
            </div>
          </div>
        </div>
      )}

      {/* Chat History Sidebar */}
      {showHistory && (
        <div className="chat-history-sidebar">
          <div className="history-header">
            <h3><i className="fas fa-history"></i> Chat History</h3>
            <button className="close-history" onClick={() => setShowHistory(false)}>
              <i className="fas fa-times"></i>
            </button>
          </div>
          <div className="history-list">
            {Object.keys(chatHistory).length === 0 ? (
              <div className="no-history">
                <i className="fas fa-inbox"></i>
                <p>No chat history yet</p>
              </div>
            ) : (
              Object.entries(chatHistory).map(([phone, session]) => (
                <div 
                  key={phone} 
                  className="history-item"
                  onClick={() => {
                    setUserPhone(phone);
                    if (session.messages && session.messages.length > 0) {
                      setMessages(session.messages);
                      setSessionId(session.sessionId);
                      setIsHumanMode(session.isHumanMode || false);
                    }
                    setShowHistory(false);
                  }}
                >
                  <div className="history-item-header">
                    <span className="history-phone">
                      <i className="fas fa-phone"></i> {formatPhone(phone)}
                    </span>
                    <span className="history-date">
                      {session.lastActive ? new Date(session.lastActive).toLocaleDateString() : ''}
                    </span>
                  </div>
                  <div className="history-preview">
                    {getSessionPreview(session)}
                  </div>
                </div>
              ))
            )}
          </div>
          {Object.keys(chatHistory).length > 0 && (
            <div className="history-footer">
              <button 
                className="clear-history-btn"
                onClick={() => {
                  const confirmed = window.confirm('Clear all chat history?');
                  if (confirmed) {
                    localStorage.removeItem(SESSIONS_STORAGE_KEY);
                    setChatHistory({});
                  }
                }}
              >
                <i className="fas fa-trash"></i> Clear All History
              </button>
            </div>
          )}
        </div>
      )}

      {/* Chat Float Button */}
      <div className={`chat-float-btn ${isOpen ? 'hidden' : ''}`} onClick={toggleChat}>
        <i className="fas fa-comment-dots"></i>
      </div>

      {/* Chat Container */}
      <div className={`chat-container ${isOpen ? 'open' : ''}`}>
        <div className="chat-header">
          <div className="header-top">
            <div className="brand">
              <div className="avatar"><i className="fas fa-gavel"></i></div>
              <div className="agent-info">
                <h3>Federal AI Assistant</h3>
                <p>
                  <span className="status-dot"></span>
                  <span>{isHumanMode ? 'Human Agent Connected' : sessionId ? 'Session Active' : 'AI Active'}</span>
                  {userPhone && userPhone !== '+233' && (
                    <span className="user-phone-badge">
                      <i className="fas fa-phone"></i> {formatPhone(userPhone)}
                    </span>
                  )}
                </p>
              </div>
            </div>
            <div className="header-actions">
              {userPhone && userPhone !== '+233' && (
                <i className="fas fa-history" onClick={() => setShowHistory(true)} title="Chat History"></i>
              )}
              <i className="fas fa-compress" onClick={toggleChat}></i>
              <i className="fas fa-times" onClick={toggleChat}></i>
            </div>
          </div>
          {queuePosition !== null && queuePosition > 0 && (
            <div className="queue-info">
              Position: #{queuePosition} | Waiting for agent...
            </div>
          )}
        </div>

        <div className="messages-area">
          {messages.map((msg, idx) => (
            <div key={idx} className={`message ${msg.role === 'user' ? 'user-message' : 'bot-message'}`}>
              <div className="bubble">{msg.content}</div>
            </div>
          ))}
          {isLoading && (
            <div className="message bot-message">
              <div className="typing-indicator">
                <span></span><span></span><span></span>
                <span>{isHumanMode ? 'agent typing...' : 'processing...'}</span>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <div className="action-buttons">
          <button className="action-btn" onClick={handleNewChat}>
            <i className="fas fa-plus-circle"></i> New Chat
          </button>
          <button className="action-btn human-btn" onClick={handleHumanAgent}>
            <i className="fas fa-headset"></i> Human Agent
          </button>
        </div>

        <div className="nav-buttons-bottom">
          {['security', 'government', 'law', 'resources'].map(nav => (
            <button 
              key={nav} 
              className={`nav-btn ${currentNav === nav ? 'active' : ''}`}
              onClick={() => handleNav(nav)}
            >
              <i className={`fas ${nav === 'security' ? 'fa-shield-alt' : nav === 'government' ? 'fa-landmark' : nav === 'law' ? 'fa-balance-scale' : 'fa-folder-open'}`}></i>
              {nav.charAt(0).toUpperCase() + nav.slice(1)}
            </button>
          ))}
        </div>

        <div className="input-area">
          <div className="input-wrapper">
            <input
              type="text"
              value={input}
              onChange={handleInputChange}
              onKeyPress={(e) => e.key === 'Enter' && handleSend()}
              placeholder="Ask about security, government..."
            />
            <button onClick={handleSend} className={isLoading ? 'disabled' : ''}>
              <i className="fas fa-paper-plane"></i>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ChatWidget;